import React, { useLayoutEffect, useRef } from 'react';
import './UniverseEntrance.css';

/** Measures actual destinations, so dealing works with every responsive grid. */
export function UniverseEntrance({ children, mode, highlightIndex = -1, onLand, onShuffle }: { children: React.ReactNode; mode: 'deal' | 'lineup'; highlightIndex?: number; onLand?: () => void; onShuffle?: () => void }) {
  const container = useRef<HTMLDivElement>(null);
  const landing = useRef(onLand);
  landing.current = onLand;
  const shuffle = useRef(onShuffle);
  shuffle.current = onShuffle;
  useLayoutEffect(() => {
    const node = container.current;
    if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !Element.prototype.animate) return;
    const elements = Array.from(node.children) as HTMLElement[];
    const bounds = node.getBoundingClientRect();
    const animations: Animation[] = [];
    const timers: number[] = [];
    if (mode === 'deal' && elements.length) timers.push(window.setTimeout(() => shuffle.current?.(), 0));
    const ordinary = elements.map((_, i) => i).filter(i => i !== highlightIndex);
    const order = highlightIndex >= 0 ? [...ordinary, highlightIndex] : ordinary;
    order.forEach((index, rank) => {
      const element = elements[index];
      if (!element) return;
      if (mode === 'deal') element.classList.add('universe-card-dealing');
      const rect = element.getBoundingClientRect();
      const delay = rank / Math.max(1, elements.length - 1) * 180;
      const x = bounds.left + bounds.width / 2 - rect.left - rect.width / 2;
      const y = bounds.top + Math.min(190, rect.height / 2) - rect.top - rect.height / 2;
      const tilt = index % 2 ? 2 : -2;
      const frames = mode === 'deal' ? [
        { transform: `translate(${x}px, ${y}px) scale(.46) rotate(${tilt}deg)`, opacity: 0, offset: 0 },
        { transform: `translate(${x}px, ${y}px) scale(.46) rotate(${tilt}deg)`, opacity: 1, offset: .1 },
        { transform: `translate(${x - 16}px, ${y}px) scale(.44) rotate(-4deg)`, offset: .22 },
        { transform: `translate(${x + 12}px, ${y}px) scale(.46) rotate(3deg)`, offset: .34 },
        { transform: `translate(0, 0) scale(1.015) rotate(${tilt}deg)`, filter: `drop-shadow(0 0 ${index === highlightIndex ? 20 : 10}px #9beaff90)`, offset: .83 },
        { transform: 'none', opacity: 1, filter: 'none', offset: 1 },
      ] : [
        { transform: `translate(${x * .3}px, 35px) scale(.92)`, opacity: 0, offset: 0 },
        { transform: `translate(${x * .12}px, -8px) rotate(${tilt}deg)`, opacity: 1, offset: .55 },
        { transform: 'none', filter: `drop-shadow(0 0 ${index === highlightIndex ? 22 : 12}px #9beaff90)`, offset: .8 },
        { transform: 'none', filter: 'none', opacity: 1, offset: 1 },
      ];
      // Segment easing preserves the actual landing timestamp at the .83 keyframe.
      animations.push(element.animate(mode === 'deal' ? frames.map(frame => ({ ...frame, easing: 'ease-in-out' })) : frames, { duration: mode === 'deal' ? 1000 : 650, delay, easing: mode === 'deal' ? 'linear' : 'cubic-bezier(.22,.7,.2,1)', fill: 'backwards' }));
      if (mode === 'deal') timers.push(window.setTimeout(() => {
        element.classList.remove('universe-card-dealing');
        landing.current?.();
      }, delay + 830));
    });
    return () => { animations.forEach(animation => animation.cancel()); timers.forEach(timer => window.clearTimeout(timer)); elements.forEach(element => element.classList.remove('universe-card-dealing')); };
  }, [mode, highlightIndex]);
  return <div ref={container} className={mode === 'deal' ? 'universe-deck-grid universe-deal-entrance' : 'team-bubbles universe-lineup-entrance'}>{children}</div>;
}
